import { useEffect } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { CheckoutFormValues, SmartformConfig } from '../types';
import { flashInputs } from '../sections/ui';

/**
 * Smartform našeptávač adres (smartform.cz) — stejná integrace jako
 * header.latte v bola-cz/bola-sk, jen pro React:
 *
 * - injektuje smartform.js (jednou), nastaví clientId a zemi instancí
 * - inputy nesou třídy smartform-instance-{fakturacni,dodaci} +
 *   smartform-{street-and-number,city,zip} (viz Billing/DeliveryAddressSection)
 * - výběr z našeptávače plní inputy přímo v DOM → hodnoty se musí propsat
 *   do react-hook-form (setValue), jinak by neprošla validace/submit
 */

declare global {
    interface Window {
        smartform?: {
            beforeInit?: () => void;
            afterInit?: () => void;
            setClientId: (id: string) => void;
            getInstance: (name: string) => { setCountry: (country: string) => void };
            rebindAllForms?: () => void;
        };
    }
}

const INSTANCES = ['smartform-instance-fakturacni', 'smartform-instance-dodaci'];

/** id inputu → cesta v react-hook-form */
const SYNCED_FIELDS: Record<string, keyof CheckoutFormValues['billing'] | string> = {
    'frm-billing-street': 'billing.street',
    'frm-billing-city': 'billing.city',
    'frm-billing-zip': 'billing.zip',
    'frm-delivery-street': 'delivery.street',
    'frm-delivery-city': 'delivery.city',
    'frm-delivery-zip': 'delivery.zip',
};

export function useSmartform(
    config: SmartformConfig | null,
    setValue: UseFormSetValue<CheckoutFormValues>,
): void {
    // 1. inicializace skriptu
    useEffect(() => {
        if (!config) {
            return;
        }

        // POZOR: smartform.js pri startu window.smartform NAHRADI vlastni instanci
        // (callbacky si prenese) - proto se na objekt musi sahat az v okamziku
        // callbacku pres window.smartform, ne pres zachycenou referenci
        window.smartform = window.smartform || ({} as NonNullable<typeof window.smartform>);

        window.smartform.beforeInit = () => {
            window.smartform?.setClientId(config.clientId);
        };
        window.smartform.afterInit = () => {
            INSTANCES.forEach((name) => {
                try {
                    window.smartform?.getInstance(name).setCountry(config.country);
                } catch {
                    // instance bez polí na stránce
                }
            });
        };

        if (document.querySelector(`script[src="${config.scriptUrl}"]`)) {
            // skript už běží (např. po client-side remountu) → přebindovat pole
            window.smartform?.rebindAllForms?.();
            return;
        }

        const script = document.createElement('script');
        script.src = config.scriptUrl;
        script.async = true;
        document.head.appendChild(script);
    }, [config]);

    // 2. sync DOM → react-hook-form: Smartform plní uncontrolled inputy přímo,
    //    posloucháme change/input a propíšeme hodnotu (+ zvalidujeme)
    useEffect(() => {
        if (!config) {
            return;
        }

        const handler = (event: Event) => {
            const target = event.target as HTMLInputElement | null;
            if (!target?.id) {
                return;
            }
            const fieldPath = SYNCED_FIELDS[target.id];
            if (!fieldPath) {
                return;
            }
            setValue(fieldPath as never, target.value as never, { shouldValidate: true, shouldDirty: true });
        };

        // Smartform po výběru adresy dispatchne change/input na doplněná pole
        // (posloucháme oba — implementace se liší podle verze skriptu)
        document.addEventListener('change', handler, true);
        document.addEventListener('input', handler, true);

        return () => {
            document.removeEventListener('change', handler, true);
            document.removeEventListener('input', handler, true);
        };
    }, [config, setValue]);
}

/** Po výběru celé adresy z našeptávače zvýrazní doplněná pole. */
export function flashAddressFields(instance: 'fakturacni' | 'dodaci'): void {
    const prefix = instance === 'fakturacni' ? 'frm-billing' : 'frm-delivery';
    flashInputs([`${prefix}-street`, `${prefix}-city`, `${prefix}-zip`]);
}
